import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  TextField,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { ListQuery, SalesDocumentViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { useTranslation } from "react-i18next";
import { CustomerAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";
interface Order {
    no: string;
    selltoCustomerNo: string;
    selltoCustomerName: string;
    selltoAddress: string;
}


const OrderList: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: Order[]; count: number }>(
                "/orderApi/GetOrderList",
                query
            );
            setOrders(response.items);
            setTotalOrders(response.count);
        } catch (error:any) {
            console.error("Failed to fetch orders:", error);
            showSnackbar(`${t("common.failedToFetchOrders")} : ${error.response?.data ?? error.message}`, "error");

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter : "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };

    const handleSelectOrder = (orderNo: string) => {
        navigate({ path: "/sales/order/edit", params: { orderNo } });
    };

    const handleCreateOrder = async () => {
        setLoading(true);

        try {
            const response = await authService.post<SalesDocumentViewModel>(
                "/orderApi/CreateOrder", {}
            );
            setLoading(false);
            navigate({ path: "/sales/order/edit", params: { orderNo: response.no } });
        } catch (error: any) {
            showSnackbar(`${t("common.failedToCreateOrder")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <CenteredListCard title={t("common.orders")}>

            <Box sx={{ marginBottom: 2 }}>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleCreateOrder}
                >
                        {t("common.create")}
                </Button>
            </Box>
            {/* Search Field */}
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_No">{t("common.number")}</option>
                                <option value="ext_SelltoCustomerNo">{t("common.customerNo")}</option>
                                <option value="ext_Email">{t("common.email")}</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>
                        {filterProperty === "ext_SelltoCustomerNo" ? (
                            <CustomerAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                        ) : (
                            <TextField
                                label={t("common.search")}
                                variant="outlined"
                                fullWidth
                                onChange={(event) => setFilter(event.target.value)}
                                value={filter}
                                disabled={loading}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
             {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>

            {/* Card Layout for Orders */}
        <LoadingSection loading={loading}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {orders.map((order) => (
                    <Grid item
                        xs={12}
                        sm={orders.length === 1 ? 12 : orders.length === 2 ? 6 : 6}
                        md={orders.length === 1 ? 12 : orders.length === 2 ? 6 : 4}
                        key={order.no}>
                        <Card
                            onClick={() => handleSelectOrder(order.no)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<ShoppingCartIcon fontSize="small" color="action" />}
                                    title={order.no}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.customerNo")}: {order.selltoCustomerNo}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.name")}: {order.selltoCustomerName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("common.address")}: {order.selltoAddress}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {orders.length === 0 && (
                    <Grid item xs={12}>
                            <Typography align="center">{t("common.noOrdersFound")}</Typography>
                    </Grid>
                )}
            </Grid>
        </LoadingSection>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalOrders}

                onPageChange={handlePageChange}

            />
         
        </CenteredListCard>
    );
};

export default OrderList;